/**
 * Add one staff member — TRUS-561.
 *
 * "Need the ability to add a staff member using the staff fields and values for
 * an admin to add new staff."
 *
 * The endpoint shipped separately and runs ONE ROW through the bulk roster
 * loader, so a person added here is built by exactly the code that built the
 * other 74: same access-level vocabulary, same E.164 phone normalisation, same
 * per-type serialisation of custom fields.
 *
 * THE FORM IS BUILT FROM THE ORG'S OWN FIELD CONFIG, not a hardcoded list. It
 * asks for the three things a login cannot exist without — name, email, access
 * level — and then for whichever staff fields that org marked mandatory.
 *
 * That last part is not tidiness. At Amigos the mandatory fields are Employee
 * ID and Date of Hire, and the hire date is what the training evaluator
 * computes due dates from: a create form that skipped it would mint staff whose
 * training can never be evaluated, which is the `unevaluated` bucket the
 * Compliance screen exists to warn about — arriving through the front door.
 *
 * It deliberately does NOT ask for every staff field. There are seventeen at
 * Amigos, most of them irrelevant on day one, and the profile editor already
 * renders all of them grouped by category. So this collects what is required
 * and then offers to open the new person's profile for the rest.
 *
 * NOBODY IS NOTIFIED. The account is created unclaimed — no password, no email
 * — exactly as the bulk loader leaves one, because creating somebody and
 * telling them are separate decisions. The invite flow is where the second one
 * happens, and the success state says so rather than leaving an admin to wonder.
 */

import { useMemo, useState } from 'react';
import { UserPlus, X } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';

interface FieldOption {
  label: string;
  value: string;
}

export interface AddStaffColumn {
  name: string;
  friendly_name: string;
  type: string;
  category: string;
  field_options?: FieldOption[] | null;
  options_source?: string | null;
  mandatory?: boolean;
}

/** The field whose options ARE the org's access-level vocabulary. */
const ACCESS_LEVEL_FIELD = 'staff_platform_access_level';

/** Asked for explicitly at the top, so never repeated in the mandatory list. */
const CORE_FIELDS = new Set([ACCESS_LEVEL_FIELD]);

function authHeaders(): Record<string, string> {
  // Carry the ACTIVE org, not just the auth token — the same headers the parent
  // Staff Profiles page sends on every call. Without X-CBO-ID the create POST is
  // scoped by get_current_cbo to the caller's DEFAULT org instead of the one they
  // are viewing: for a super_admin with no org membership that fallback is the
  // first active org (cbo 1, "Trust Care Organization"), which has no
  // staff_access_level_map. So an admin org-switched into an org whose roster
  // loads fine still has their staff add rejected with "No staff access levels
  // are configured for this organization" — against the wrong org entirely. The
  // roster GET sent X-CBO-ID and succeeded; the create POST omitted it and did
  // not. (TRUS-570)
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const cboId = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
}

export function AddStaffDialog({
  open,
  columns,
  staffOptions,
  onClose,
  onCreated,
}: {
  open: boolean;
  columns: AddStaffColumn[];
  /** For a field whose options are the org staff list (e.g. Manager Name). */
  staffOptions: FieldOption[];
  onClose: () => void;
  onCreated: (userId: number, name: string) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [accessLevel, setAccessLevel] = useState('');
  const [phone, setPhone] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accessLevels = useMemo<FieldOption[]>(() => {
    const field = columns.find((c) => c.name === ACCESS_LEVEL_FIELD);
    return (field?.field_options || []) as FieldOption[];
  }, [columns]);

  // Whichever fields THIS org made mandatory. An org that marks none gets the
  // core three and nothing else, which is correct rather than a degraded form.
  const requiredFields = useMemo(
    () => columns.filter((c) => c.mandatory && !CORE_FIELDS.has(c.name)),
    [columns],
  );

  const missing = useMemo(() => {
    const out: string[] = [];
    if (!name.trim()) out.push('Name');
    if (!email.trim()) out.push('Email');
    if (!accessLevel) out.push('Trust Access Level');
    for (const f of requiredFields) {
      if (!(values[f.name] || '').trim()) out.push(f.friendly_name);
    }
    return out;
  }, [name, email, accessLevel, values, requiredFields]);

  const reset = () => {
    setName('');
    setEmail('');
    setAccessLevel('');
    setPhone('');
    setValues({});
    setError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (missing.length > 0) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(getWeaveinApiUrl('/api/staff-profiles'), {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          access_level: accessLevel,
          phone_number: phone.trim() || null,
          values,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        // The server distinguishes "already on your staff list" from "has an
        // account in another organization" — they are different problems and
        // the admin needs to be told which.
        const detail = data?.detail;
        throw new Error(
          typeof detail === 'string'
            ? detail
            : detail?.message || `Could not add the staff member (${res.status}).`,
        );
      }
      onCreated(data.user_id, name.trim());
      reset();
    } catch (e: any) {
      setError(e.message || 'Could not add the staff member.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const renderFieldInput = (f: AddStaffColumn) => {
    const base = 'w-full border border-v2border rounded-md px-3 py-2 text-sm';
    const v = values[f.name] || '';
    const set = (next: string) => setValues((prev) => ({ ...prev, [f.name]: next }));

    if (f.type === 'date') {
      return <input type="date" className={base} value={v} onChange={(e) => set(e.target.value)} />;
    }
    if (f.type === 'select') {
      const opts = f.options_source === 'org_staff' ? staffOptions : f.field_options || [];
      return (
        <select className={base} value={v} onChange={(e) => set(e.target.value)}>
          <option value="">—</option>
          {opts.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }
    return <input className={base} value={v} onChange={(e) => set(e.target.value)} />;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/40 p-4 sm:p-8">
      <div className="w-full max-w-xl rounded-[10px] border border-v2border bg-surface shadow-v2-1 my-4">
        <div className="flex items-start justify-between gap-4 border-b border-v2border px-5 py-4">
          <div>
            <div className="font-semibold text-ink">Add staff member</div>
            <div className="mt-1 text-[13px] text-ink-500">
              Creates the account and their profile. They are not notified — send an invitation when you are ready.
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="rounded-md p-1 text-ink-400 hover:bg-paper-2 hover:text-ink"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

          <label className="block">
            <span className="mb-1 block text-[13px] font-medium text-ink">
              Full name<span className="ml-1 text-red-600">*</span>
            </span>
            <input
              className="w-full border border-v2border rounded-md px-3 py-2 text-sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-[13px] font-medium text-ink">
              Work email<span className="ml-1 text-red-600">*</span>
            </span>
            <input
              type="email"
              className="w-full border border-v2border rounded-md px-3 py-2 text-sm"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jdoe@example.org"
            />
            <span className="mt-1 block text-[12px] text-ink-400">
              This is their sign-in, and how the invitation reaches them.
            </span>
          </label>

          <label className="block">
            <span className="mb-1 block text-[13px] font-medium text-ink">
              Trust Access Level<span className="ml-1 text-red-600">*</span>
            </span>
            <select
              className="w-full border border-v2border rounded-md px-3 py-2 text-sm"
              value={accessLevel}
              onChange={(e) => setAccessLevel(e.target.value)}
            >
              <option value="">—</option>
              {accessLevels.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-[12px] text-ink-400">
              Decides what they can see and do. Your organization&apos;s own levels.
            </span>
          </label>

          <label className="block">
            <span className="mb-1 block text-[13px] font-medium text-ink">Mobile number</span>
            <input
              className="w-full border border-v2border rounded-md px-3 py-2 text-sm"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 408 555 1212"
            />
            <span className="mt-1 block text-[12px] text-ink-400">
              Optional, and used for two-factor sign-in codes.
            </span>
          </label>

          {requiredFields.map((f) => (
            <label key={f.name} className="block">
              <span className="mb-1 block text-[13px] font-medium text-ink">
                {f.friendly_name}
                <span className="ml-1 text-red-600">*</span>
              </span>
              {renderFieldInput(f)}
              {f.name === 'staff_date_of_hire' && (
                <span className="mt-1 block text-[12px] text-ink-400">
                  Training due dates are calculated from this, so it cannot be filled in later without their
                  compliance showing as unevaluated.
                </span>
              )}
            </label>
          ))}

          <p className="text-[12px] text-ink-400">
            Everything else can be filled in on their profile once they exist.
          </p>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-v2border px-5 py-3">
          <span className="text-[12px] text-ink-400">
            {missing.length > 0 ? `Still needed: ${missing.join(', ')}` : ' '}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={close}
              disabled={saving}
              className="rounded-md px-3 py-1.5 text-[13px] font-medium text-ink-700 hover:bg-paper-2 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={saving || missing.length > 0}
              className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              <UserPlus className="h-4 w-4" />
              {saving ? 'Adding…' : 'Add staff member'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddStaffDialog;
